import { createSelector } from "@reduxjs/toolkit";
import { uniq } from "lodash";

import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { type RootState } from "@ds/store/bootstrap";
import { selectorsExtractIds, selectorsOutputEntitiesByIds } from "@ds/store/global-selectors";

import { getAggregatedModel } from "@ds/utils/entities";
import { LoadStatus } from "@ds/utils/reducer";

import { CampaignItemsRemoteOperation, entitySelectors } from "./slice";

export const selectBaseState = (state: RootState) => state.content.campaignItems;
const selectLoadStatus = (state: RootState, operation = CampaignItemsRemoteOperation.CRUD) =>
  selectBaseState(state).loadStatusMap[operation];

export const selectCampaignItemsIsLoading = (state: RootState, operation = CampaignItemsRemoteOperation.CRUD) =>
  selectLoadStatus(state, operation) === LoadStatus.Loading;

export const selectCampaignItemsError = (state: RootState) => selectBaseState(state).error;

export const selectTableCampaignItemIds = (
  state: RootState,
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = DetailsTableDataTypeEnum.ContentCampaignItems,
) => selectBaseState(state).tableIds[tableType];

export const selectSelectedCampaignItemIds = (
  state: RootState,
  tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum = DetailsTableDataTypeEnum.ContentCampaignItems,
) => selectBaseState(state).selectedIds[tableType];

export const selectCampaignItemById = (state: RootState, id: number) =>
  entitySelectors.selectById(selectBaseState(state), id) as CampaignItem;

export const selectCampaignItemIds = (state: RootState) => entitySelectors.selectIds(selectBaseState(state));
export const selectCampaignItems = (state: RootState) => entitySelectors.selectAll(selectBaseState(state));

export const selectCampaignItemsByIds = createSelector(
  selectBaseState,
  selectorsExtractIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectTableCampaignItems = createSelector(
  selectBaseState,
  selectTableCampaignItemIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectSelectedCampaignItems = createSelector(
  selectBaseState,
  selectSelectedCampaignItemIds,
  selectorsOutputEntitiesByIds(() => entitySelectors),
);

export const selectAggregatedCampaignItemByIds = createSelector(selectBaseState, selectorsExtractIds, (state, ids) =>
  ids?.length ? getAggregatedModel(selectorsOutputEntitiesByIds(entitySelectors)(state, ids)) : null,
);

export const selectAggregatedCampaignItemForSelectedEntities = createSelector(
  selectSelectedCampaignItems,
  selectedCampaignItems => getAggregatedModel(selectedCampaignItems),
);

export const selectSelectedCampaignItemIndex = createSelector(
  selectTableCampaignItems,
  selectAggregatedCampaignItemForSelectedEntities,
  (tableItems, selectedItem) => (selectedItem ? tableItems.findIndex(item => item.id === selectedItem.id) : -1),
);

export const selectCampaignItemsByCampaignId = createSelector(
  selectBaseState,
  selectorsExtractIds,
  (state, campaignIds) => {
    const normalizedCampaignIds = uniq(campaignIds as number[]);
    return entitySelectors.selectAll(state).filter(({ campaign_id }) => normalizedCampaignIds.includes(campaign_id));
  },
);
